import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Sparkles, Loader2, Plus, ChevronDown, ChevronUp } from 'lucide-react';

export default function StarterPacks({ onImported, emphasize = false }) {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [open, setOpen] = useState(true);

  const load = useCallback(async () => {
    try { const r = await api.get('/bundles'); setBundles(r.data?.bundles || []); }
    catch (e) { console.debug('bundles load failed', e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addPack = async (b) => {
    setBusyId(b.id);
    try {
      const res = await api.post(`/bundles/${b.id}/import`);
      const n = res.data?.agents?.length || 0;
      toast.success(`Added ${n} agent${n === 1 ? '' : 's'} from "${b.name}".`);
      onImported && onImported(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add that pack');
    } finally { setBusyId(null); }
  };

  if (loading || bundles.length === 0) return null;

  return (
    <div className="px-6 pt-5" data-testid="starter-packs">
      {emphasize && (
        <div className="mb-4 rounded-2xl border border-[hsl(var(--border))] p-4" style={{ background: 'hsl(var(--accent))' }} data-testid="starter-packs-nudge">
          <p className="font-display font-600 flex items-center gap-2"><Sparkles className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} /> New here? Add a starter pack to get going in one click.</p>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5">Pick a set of ready-made agents below — you can edit or delete them anytime.</p>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} />
          <h2 className="font-display font-600 text-sm">Starter packs</h2>
          <span className="text-[11px] text-[hsl(var(--muted-foreground))]">— add a ready-made set of agents in one click</span>
        </div>
        <button onClick={() => setOpen((v) => !v)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] flex items-center gap-1 text-xs" data-testid="starter-packs-toggle">
          {open ? <>Hide <ChevronUp className="h-3.5 w-3.5" /></> : <>Show <ChevronDown className="h-3.5 w-3.5" /></>}
        </button>
      </div>

      {open && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {bundles.map((b) => (
            <div key={b.id} className="rounded-2xl border border-[hsl(var(--border))] bg-card p-4 flex flex-col" data-testid={`bundle-card-${b.id}`}>
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl grid place-items-center text-lg" style={{ background: (b.color || '#0e9488') + '22' }}>{b.emoji}</div>
                <p className="font-600 text-sm leading-tight">{b.name}</p>
              </div>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2 line-clamp-2">{b.description}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {b.agents.map((ag) => (
                  <span key={ag.name} className="text-[10px] px-1.5 py-0.5 rounded-md bg-[hsl(var(--surface-2))] flex items-center gap-1" title={ag.description}>
                    <span>{ag.avatar}</span>{ag.name}
                  </span>
                ))}
              </div>
              <Button
                variant="secondary"
                className="rounded-xl mt-3 w-full border border-[hsl(var(--border))] gap-1.5"
                onClick={() => addPack(b)}
                disabled={busyId === b.id}
                data-testid={`add-bundle-${b.id}`}
              >
                {busyId === b.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Add pack ({b.agent_count})
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
